import React, { useContext } from "react";
import Tooltip from '@mui/material/Tooltip';

import "./cart-summary-style.scss";
import { DeviceContext, deviceType } from '@providers';

import Button from "@src/componentsNew/Buttons/Button";
import Typography from "@src/componentsNew/Typography/Typography";
import { MgsShippingChargeTooltip } from "@atoms/MgsShippingChargeTooltip";
import { SimplePricePreview } from "@atoms/SimplPricePreview";
import InfoIcon from "@src/componentsNew/Icons/Icons/InfoIcon";
import ArrowForwardIcon from "@src/componentsNew/Icons/Icons/ArrowForwardIcon";
import ExpandLessIcon from "@src/componentsNew/Icons/Icons/ExpandLessIcon";

import { cart_cart_shippingPrice, cart_cart_totalWeightSelected } from "@src/pages/Cart/__generated__/cart";
import { formatPrice, isBeautyDeviceProductInSelectedCart } from "@src/utils";

interface IProps {
  activeCart: "maccaron" | "mgs";
  totalPrice: number;
  totalDiscount: number;
  handleClick: any;
  loading: boolean;
  isSomeChecked: boolean;
  totalWeightSelected: cart_cart_totalWeightSelected;
  shippingPrice: cart_cart_shippingPrice;
  selectedProductIDs: string[];
}

const CartSummary = ({
  activeCart,
  totalPrice,
  totalDiscount,
  handleClick,
  loading,
  isSomeChecked,
  totalWeightSelected,
  shippingPrice,
  selectedProductIDs
}: IProps) => {
  const { isMobile } = useContext<deviceType>(DeviceContext);

  const subTotalPrice = totalPrice - totalDiscount;
  const isMGSTotalWeightOverMax = totalWeightSelected.value > Number(sessionStorage.getItem("mgsMaximumOrderWeightValue"));

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className={`cart-summary-wrapper ${isMobile ? 'mobile' : ''}`}>
      <div className="cart-price-info-wrapper">
        {
          isMobile && (
            <Typography variant="body1" component="span" style={{ color: "#212121" }}>
              Summary
            </Typography>
          )
        }

        <div className="cart-price-summary-area">
          <div className="price-wrapper">
            <Typography variant={isMobile ? "body3" : "body1"} component="span" style={{ color: "#212529" }}>
              Retail Price
            </Typography>

            <Typography variant={isMobile ? "body1" : "price"} component="span" style={{ color: "#212529" }}>
              {formatPrice(totalPrice)}
            </Typography>
          </div>

          {
            totalDiscount > 0 && (
              <div className="price-wrapper">
                <Typography variant={isMobile ? "body3" : "body1"} component="span" style={{ color: "#FF3398" }}>
                  Saved
                </Typography>

                <Typography variant={isMobile ? "body1" : "price"} component="span" style={{ color: "#FF3398" }}>
                  {`-${formatPrice(totalDiscount)}`}
                </Typography>
              </div>
            )
          }

          <div className="price-wrapper">
            <Typography variant={isMobile ? "subtitle2" : "body1"} component="span" style={{ color: "#212529" }}>
              Subtotal
            </Typography>

            <Typography variant="price" component="span" style={{ color: "#212529" }}>
              {formatPrice(subTotalPrice)}
            </Typography>
          </div>

          <div className="price-wrapper">
            <div className="icon-text-wrapper">
              <Typography variant={isMobile ? "body3" : "body1"} component="span" style={{ color: "#212529" }}>
                Estimated Shipping Fee
              </Typography>
              {activeCart === "mgs" && <MgsShippingChargeTooltip />}
            </div>

            <Typography variant={isMobile ? "body1" : "price"} component="span" style={{ color: "#212529" }}>
              {formatPrice(shippingPrice.amount)}
            </Typography>
          </div>
        </div>

        {
          !isMobile && (
            <>
              <div className="divider-line" />

              {
                activeCart === "mgs" && (
                  <>
                    <div className="mgs-weight-info-wrapper">
                      <div className="price-wrapper">
                        <div className="icon-text-wrapper">
                          {
                            isMGSTotalWeightOverMax ? (
                              <Typography variant="body2" component="span" style={{ color: "#FF3D00" }}>
                                Exceed orderable weight
                              </Typography>
                            ) : (
                              <Typography variant="body2" component="span" style={{ color: "#1E8623" }}>
                                Order available
                              </Typography>
                            )
                          }

                          <Tooltip
                            title={
                              `As per Macaron policy, there is a weight limit per purchase for MGS items. 
                      Please refer to the product weight information and place an order not to exceed
                      ${sessionStorage.getItem("mgsMaximumOrderWeightValue")}${sessionStorage.getItem("mgsMaximumOrderWeightUnit")}.`
                            }
                            placement="bottom"
                            arrow>
                            <div>
                              <InfoIcon />
                            </div>
                          </Tooltip>
                        </div>

                        <Typography variant="body2" component="span" style={{ color: isMGSTotalWeightOverMax ? "#FF3D00" : "#1E8623" }}>
                          {totalWeightSelected.value + totalWeightSelected.unit} / {sessionStorage.getItem("mgsMaximumOrderWeightValue")}{sessionStorage.getItem("mgsMaximumOrderWeightUnit")}
                        </Typography>
                      </div>

                      {
                        isMGSTotalWeightOverMax && (
                          <Typography cName="weight-reduce-text" variant="body2" component="span" style={{ color: "#9E9E9E" }}>
                            * Please reduce the weight to {sessionStorage.getItem("mgsMaximumOrderWeightValue")}{sessionStorage.getItem("mgsMaximumOrderWeightUnit")} or less
                          </Typography>
                        )
                      }
                    </div>

                    <div className="divider-line" />
                  </>
                )
              }

              <div className="price-wrapper">
                <Typography variant="h8" component="span" style={{ color: "#212529" }}>
                  Total
                </Typography>

                <Typography variant="price" component="span" style={{ color: "#212529" }}>
                  {formatPrice(subTotalPrice + shippingPrice.amount)}
                </Typography>
              </div>
            </>
          )
        }

        <div className={`info-text-wrapper ${isMobile ? "mobile" : ""}`}>
          <Typography cName="tax-text" variant="body2" component="span" style={{ color: "#11141D99" }}>
            All taxes included
          </Typography>

          <SimplePricePreview price={subTotalPrice + shippingPrice.amount} isBeautyDeviceProductInSelectedCart={isBeautyDeviceProductInSelectedCart(selectedProductIDs)} />

          <Typography variant="caption" component="span" style={{ color: "#6A29F5" }}>
            * Vouchers can be applied on the checkout page.
          </Typography>
        </div>
      </div>

      {
        isMobile ? (
          <>
            {/* TODO: COD, Voucher 등 문구 */}

            <div className="checkout-button-wrapper mobile">
              {
                (isSomeChecked && activeCart === "mgs") && (
                  <>
                    <div className="mgs-weight-info-wrapper">
                      <div className="price-wrapper">
                        <div className="icon-text-wrapper">
                          {
                            isMGSTotalWeightOverMax ? (
                              <Typography variant="body2" component="span" style={{ color: "#FF3D00" }}>
                                Exceed orderable weight
                              </Typography>
                            ) : (
                              <Typography variant="body2" component="span" style={{ color: "#1E8623" }}>
                                Order available
                              </Typography>
                            )
                          }

                          <Tooltip
                            title={
                              `As per Macaron policy, there is a weight limit per purchase for MGS items. 
                      Please refer to the product weight information and place an order not to exceed
                      ${sessionStorage.getItem("mgsMaximumOrderWeightValue")}${sessionStorage.getItem("mgsMaximumOrderWeightUnit")}.`
                            }
                            placement="bottom"
                            arrow>
                            <div>
                              <InfoIcon />
                            </div>
                          </Tooltip>
                        </div>

                        <Typography variant="body2" component="span" style={{ color: isMGSTotalWeightOverMax ? "#FF3D00" : "#1E8623" }}>
                          {totalWeightSelected.value + totalWeightSelected.unit} / {sessionStorage.getItem("mgsMaximumOrderWeightValue")}{sessionStorage.getItem("mgsMaximumOrderWeightUnit")}
                        </Typography>
                      </div>

                      {
                        isMGSTotalWeightOverMax && (
                          <Typography cName="weight-reduce-text" variant="body2" component="span" style={{ color: "#9E9E9E" }}>
                            * Please reduce the weight to {sessionStorage.getItem("mgsMaximumOrderWeightValue")}{sessionStorage.getItem("mgsMaximumOrderWeightUnit")} or less
                          </Typography>
                        )
                      }
                    </div>
                  </>
                )
              }

              {
                isSomeChecked && (
                  <div className="price-wrapper">
                    <div className="mobile-total-text-wrapper">
                      <Typography variant="body3" component="span" style={{ color: "#090A0FDE" }}>
                        Total
                      </Typography>

                      <Typography variant="subtitle1" component="span" style={{ color: "#090A0FDE" }}>
                        {formatPrice(subTotalPrice + shippingPrice.amount)}
                      </Typography>
                    </div>

                    <div className="mobile-total-text-wrapper" onClick={scrollToBottom}>
                      <Typography variant="subtitle1" component="span" style={{ color: "#090A0FDE" }}>
                        Summary
                      </Typography>

                      <ExpandLessIcon />
                    </div>
                  </div>
                )
              }

              {
                (!isSomeChecked || subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit"))) && (
                  <div className="checkout-disable-text-wrapper">
                    {
                      !isSomeChecked &&
                      <div className="icon-text-wrapper">
                        <InfoIcon />

                        <Typography variant="body2" component="span" style={{ color: "#9E9E9E" }}>
                          Please select product to place order.
                        </Typography>
                      </div>
                    }

                    {
                      subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit")) ?
                        <div className="icon-text-wrapper">
                          <InfoIcon />

                          <Typography variant="body2" component="span" style={{ color: "#FF3D00" }}>
                            {`Orders over ₹${Number(sessionStorage.getItem("purchaseLimit")) ? Number(sessionStorage.getItem("purchaseLimit")).toLocaleString() : "10,000"} are not possible.`}
                          </Typography>
                        </div> : null
                    }
                  </div>
                )
              }
              <Button
                cName={`go-to-checkout-button ${activeCart}`}
                variant="contained"
                size="large"
                disabled={
                  loading ||
                  !isSomeChecked ||
                  subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit")) ||
                  (activeCart === "mgs" && isMGSTotalWeightOverMax)
                }
                endIcon={<ArrowForwardIcon fillColor="#FFFFFF" />}
                onClick={handleClick}>
                Checkout
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="checkout-button-wrapper">
              <Button
                cName={`go-to-checkout-button ${activeCart}`}
                variant="contained"
                size="large"
                disabled={
                  loading ||
                  !isSomeChecked ||
                  subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit")) ||
                  (activeCart === "mgs" && isMGSTotalWeightOverMax)
                }
                endIcon={<ArrowForwardIcon fillColor="#FFFFFF" />}
                onClick={handleClick}>
                Checkout
              </Button>

              {
                (!isSomeChecked || subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit"))) && (
                  <div className="checkout-disable-text-wrapper">
                    {
                      !isSomeChecked &&
                      <div className="icon-text-wrapper">
                        <InfoIcon />

                        <Typography variant="body2" component="span" style={{ color: "#9E9E9E" }}>
                          Please select product to place order.
                        </Typography>
                      </div>
                    }

                    {
                      subTotalPrice >= Number(sessionStorage.getItem("purchaseLimit")) ?
                        <div className="icon-text-wrapper">
                          <InfoIcon />

                          <Typography variant="body2" component="span" style={{ color: "#FF3D00" }}>
                            {`Orders over ₹${Number(sessionStorage.getItem("purchaseLimit")) ? Number(sessionStorage.getItem("purchaseLimit")).toLocaleString() : "10,000"} are not possible.`}
                          </Typography>
                        </div> : null
                    }
                  </div>
                )
              }
            </div>

            {/* TODO: COD, Voucher 등 문구 */}
          </>
        )
      }
    </div >
  );
};

export default CartSummary;
